import type { Disposable } from '../types/Disposable.js';

export class AutoDisposableList {
  private readonly disposables = new Set<Disposable>();
  private readonly finalDisposables: Disposable[] = [];

  /**
   * Add an item to dispose. This will be disposed in parallel with other items.
   * (Use `addFinal` if order matters.)
   */
  public add<T extends Disposable>(item: T): T {
    this.disposables.add(item);
    return item;
  }

  /**
   * Add a disposable that will be handled at the end and run in series.
   * This is mainly useful for disposing the reporter after anything else that might log.
   */
  public addFinal<T extends Disposable>(item: T): T {
    if (!this.finalDisposables.includes(item)) {
      this.finalDisposables.push(item);
    }
    return item;
  }

  /**
   * Dispose all registered disposables.
   * If any disposables throw, it will be logged, and a single error will be thrown at the end.
   *
   * After each call, the registered disposables will be cleared, so the list can be reused.
   */
  public async dispose(): Promise<void> {
    let errorCount = 0;

    async function doDispose(d: Disposable) {
      try {
        await d.dispose?.();
      } catch (err) {
        console.error(`Failed to dispose: ${(err as Error).stack || err}`);
        errorCount++;
      }
    }

    const disposables = Array.from(this.disposables);
    await Promise.all(disposables.map((d) => doDispose(d)));

    // Clear the set after attempting to dispose all items
    this.disposables.clear();

    for (const d of this.finalDisposables) {
      await doDispose(d);
    }
    this.finalDisposables.length = 0;

    if (errorCount) {
      throw new Error(`Failed to dispose ${errorCount} disposable(s)`);
    }
  }
}
